import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import type { ShippingLineDTO } from '@uturn/api/v1';
import {
	type FormValues,
	PORT_MAX_LENGTH,
	VESSEL_NAME_MAX_LENGTH,
} from '@uturn/portal/modules/shipments/schema';
import {
	isFieldReadOnly,
	shouldUpdate,
} from '@uturn/portal/modules/shipments/utils';
import { isSelectableShippingLine } from '@uturn/portal/types/shipping-line.typeguard';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	Combobox,
	FormControl,
	FormField,
	FormFieldReadOnly,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
} from '@uturn/ui-kit';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CharacterHint } from '../../character-hint';

export type ShipInformationProps = {
	readOnlyFields?: string[];
	shippingLines: ShippingLineDTO[];
	indexInLocations?: number;
};

export function ShipInformation({
	readOnlyFields = [],
	shippingLines,
	indexInLocations,
}: ShipInformationProps) {
	const { t } = useTranslation();
	const form = useFormContext<FormValues>();

	const fieldRef = (name: string) => {
		return `locations.${indexInLocations}.${name}` as keyof FormValues; // as const;
	};

	const hasData = (): boolean => {
		const fields = ['shippingLineId', 'port', 'vesselName'];
		return fields.some(
			(field) => !!form.getValues(fieldRef(`action.${field}`)),
		);
	};

	const [collapsed, setCollapsed] = useState(!hasData());

	return (
		<div className="sm:col-span-full">
			<Accordion type="single" collapsible value={collapsed ? '' : 'ship-info'}>
				<AccordionItem value="ship-info" className="border-0">
					<AccordionPrimitive.Header className="flex">
						<AccordionPrimitive.Trigger
							className={
								'flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180'
							}
							onClick={() => setCollapsed(!collapsed)}
						>
							<div className="flex items-center pb-3 text-sm font-medium leading-6">
								<Icon
									icon={collapsed ? faPlus : faMinus}
									className="ml-1 mr-2 h-5"
								/>
								{t(
									'pages.shipment-details.add-ship-information.button',
									'Add ship information',
								)}
							</div>
						</AccordionPrimitive.Trigger>
					</AccordionPrimitive.Header>
					<AccordionContent>
						<div className="grid grid-cols-1 gap-5 sm:grid-cols-6">
							<div className="sm:col-span-full">
								<FormField
									control={form.control}
									name={fieldRef('action.shippingLineId')}
									render={({ field }) => (
										<FormItem className="flex flex-col">
											<FormLabel>
												{t(
													'pages.shipment-details.add-ship-information.shipping-line.label',
													'Shipping Line',
												)}
											</FormLabel>
											{isFieldReadOnly(
												readOnlyFields,
												'locationActions.shippingLineId',
											) ? (
												<FormFieldReadOnly
													value={
														form.getValues(fieldRef('action.shippingLine'))
															?.description ?? '-'
													}
												/>
											) : (
												<FormControl>
													<Combobox
														defaultValue={field.value as number} // Guaranteed by options.value (shippingLine.id)
														onChange={field.onChange}
														options={shippingLines
															.filter(isSelectableShippingLine)
															.map((item) => ({
																value: item.id,
																label: item.description,
																onSelect: () =>
																	form.setValue(
																		fieldRef('action.shippingLine'),
																		item,
																		shouldUpdate,
																	),
															}))}
														placeholder={t(
															'pages.shipment-details.add-ship-information.shipping-line.placeholder',
															'Select shipping line',
														).toString()}
														searchPlaceholder={t(
															'pages.shipment-details.add-ship-information.shipping-line.dropdown.placeholder',
															'Search shipping line...',
														).toString()}
														emptyMessage={t(
															'pages.shipment-details.add-ship-information.shipping-line.dropdown.no-results',
															'No shipping line found.',
														).toString()}
													/>
												</FormControl>
											)}
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="sm:col-span-3">
								<FormField
									control={form.control}
									name={fieldRef('action.vesselName')}
									render={({ field }) => {
										const value =
											typeof field.value === 'string' ? field.value : '';
										return (
											<FormItem>
												<FormLabel className="flex items-center">
													{t(
														'pages.shipment-details.add-shipment-location.vessel-name',
														'Vessel name',
													)}
												</FormLabel>
												{isFieldReadOnly(
													readOnlyFields,
													'locationActions.vesselName',
												) ? (
													<FormFieldReadOnly value={value || '-'} />
												) : (
													<>
														<FormControl>
															<Input
																{...field}
																value={value}
																maxLength={VESSEL_NAME_MAX_LENGTH}
															/>
														</FormControl>
														<CharacterHint
															currentCount={value.length}
															maxCharacters={VESSEL_NAME_MAX_LENGTH}
														/>
													</>
												)}
												<FormMessage />
											</FormItem>
										);
									}}
								/>
							</div>
							<div className="sm:col-span-3">
								<FormField
									control={form.control}
									name={fieldRef('action.port')}
									render={({ field }) => {
										const value =
											typeof field.value === 'string' ? field.value : '';
										return (
											<FormItem>
												<FormLabel className="flex items-center">
													{t(
														'pages.shipment-details.add-shipment-location.port-of-origin',
														'Port of origin',
													)}
												</FormLabel>
												{isFieldReadOnly(
													readOnlyFields,
													'locationActions.portOfOrigin',
												) ? (
													<FormFieldReadOnly value={value || '-'} />
												) : (
													<>
														<FormControl>
															<Input
																{...field}
																value={value}
																maxLength={PORT_MAX_LENGTH}
															/>
														</FormControl>
														<CharacterHint
															currentCount={value.length}
															maxCharacters={PORT_MAX_LENGTH}
														/>
													</>
												)}
												<FormMessage />
											</FormItem>
										);
									}}
								/>
							</div>
						</div>
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		</div>
	);
}
